import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle2 } from "lucide-react";

export default function ReviewPage() {
  const { token } = useParams();
  const { t } = useApp();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [f, setF] = useState({ rating: 5, text: "", name: "", city: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/public/review/${token}`).then((r) => { setInfo(r.data); setF((x) => ({ ...x, name: r.data.booking.customer_name })); }).catch(() => setErr(true)); }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/public/review/${token}`, f); setInfo({ ...info, review: data.testimonial }); toast.success(t("review_thanks")); }
    catch (er) { toast.error(er?.response?.data?.detail || t("failed")); } finally { setBusy(false); }
  };
  if (err) return <div className="py-20 text-center text-slate-500" data-testid="review-invalid">{t("review_invalid")}</div>;
  if (!info) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  const { booking: b, review } = info;
  return (
    <div className="max-w-xl mx-auto px-4 py-12" data-testid="review-page">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">{t("review_title")}</div>
      <h1 className="text-3xl font-bold">{t("review_heading")}</h1>
      <p className="text-slate-500 mt-2">{t("booking_code")}: <b className="font-mono">{b.code}</b> · {(b.trips || []).map((tr) => tr.route).join(", ")}</p>
      {review ? (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="review-done">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mb-2" />
          <div className="font-bold text-lg">{t("review_thanks")}</div>
          <p className="text-slate-600 text-sm mt-1">{review.status === "approved" ? t("review_published") : t("review_pending")}</p>
          <div className="mt-4 flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-5 h-5 ${i < review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
          <p className="mt-2 text-slate-700">“{review.text}”</p>
          <Link to="/" className="inline-block mt-4 text-emerald-700 text-sm font-semibold">← {t("home")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5 abdan-card p-6">
          <div><Label>{t("rating")}</Label>
            <div className="flex gap-1 mt-1" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} data-testid={`review-star-${n}`} className="p-1"><Star className={`w-8 h-8 transition ${n <= f.rating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} /></button>)}
            </div></div>
          <div><Label>{t("review_text")}</Label><Textarea rows={4} required minLength={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder={t("review_ph")} data-testid="review-text" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>{t("name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name" /></div>
            <div><Label>{t("city")}</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Jakarta" data-testid="review-city" /></div>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="review-submit">{busy ? "..." : t("send_review")}</Button>
        </form>
      )}
    </div>
  );
}
